import trustFeature from "@/assets/blog/trust-is-earned-feature.svg";
import agentPodsFeature from "@/assets/blog/5-agent-pods-feature.svg";
import deliveryGapFeature from "@/assets/blog/delivery-intelligence-gap-feature.svg";

export type BlogLens = "strategy" | "engineering" | "design" | "marketing";
export type BlogPlatform =
  | "drupal"
  | "acquia"
  | "hubspot"
  | "aws"
  | "azure"
  | "gcp"
  | "wordpress"
  | "nextjs"
  | "react"
  | "contentful"
  | "salesforce"
  | "mautic"
  | "marketo";
export type BlogIndustry =
  | "non-profit"
  | "higher-ed"
  | "retail"
  | "healthcare"
  | "financial-services"
  | "technology"
  | "media"
  | "manufacturing"
  | "public-sector"
  | "sports-entertainment";

export interface BlogPost {
  slug: string;
  label: string;
  title: string;
  subtitle: string;
  metaDescription: string;
  excerpt: string;
  date: string;
  readTime: string;
  author: {
    name: string;
    role: string;
  };
  featureImage: string;
  content: string;
  cta: {
    text: string;
    link: string;
  };
  lenses?: BlogLens[];
  platforms?: BlogPlatform[];
  industries?: BlogIndustry[];
}

// "marketing" lens is labelled "RevOps & Growth" per brand positioning.
export const LENS_OPTIONS: { value: BlogLens; label: string }[] = [
  { value: "strategy", label: "Strategy" },
  { value: "design", label: "Design" },
  { value: "engineering", label: "Engineering" },
  { value: "marketing", label: "RevOps & Growth" },
];

export const PLATFORM_OPTIONS: { value: BlogPlatform; label: string }[] = [
  { value: "drupal", label: "Drupal" },
  { value: "acquia", label: "Acquia" },
  { value: "wordpress", label: "WordPress" },
  { value: "nextjs", label: "Next.js" },
  { value: "react", label: "React" },
  { value: "contentful", label: "Contentful" },
  { value: "hubspot", label: "HubSpot" },
  { value: "salesforce", label: "Salesforce" },
  { value: "marketo", label: "Marketo" },
  { value: "mautic", label: "Mautic" },
  { value: "aws", label: "AWS" },
  { value: "azure", label: "Azure" },
  { value: "gcp", label: "Google Cloud" },
];

export const INDUSTRY_OPTIONS: { value: BlogIndustry; label: string }[] = [
  { value: "non-profit", label: "Non-profit" },
  { value: "higher-ed", label: "Higher Ed" },
  { value: "healthcare", label: "Healthcare" },
  { value: "retail", label: "Retail" },
  { value: "financial-services", label: "Financial Services" },
  { value: "technology", label: "Technology" },
  { value: "media", label: "Media & Publishing" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "public-sector", label: "Public Sector" },
  { value: "sports-entertainment", label: "Sports & Entertainment" },
];

// Label resolvers used across case-study + blog surfaces so a stored value
// like "marketing" always renders as "RevOps & Growth".
export function labelForLens(v: BlogLens): string {
  return LENS_OPTIONS.find((o) => o.value === v)?.label ?? v;
}
export function labelForPlatform(v: BlogPlatform): string {
  return PLATFORM_OPTIONS.find((o) => o.value === v)?.label ?? v;
}
export function labelForIndustry(v: BlogIndustry): string {
  return INDUSTRY_OPTIONS.find((o) => o.value === v)?.label ?? v;
}

export const blogPosts: BlogPost[] = [
  {
    slug: "trust-is-earned-not-assumed",
    label: "Delivery",
    title: "Trust Is Earned, Not Assumed: Why Invisible AI Is the Only Way to Start",
    subtitle:
      "The three-stage trust model that governs how agents earn visibility and autonomy in production environments - and why every approach that skips trust governance will fail.",
    metaDescription:
      "Most AI deployments skip trust governance entirely. Here's the three-stage model we use to let agents earn autonomy - not assume it.",
    excerpt:
      "Most teams either hide AI involvement entirely or deploy agents with full autonomy from day one. Neither works. We built a three-stage trust model - Invisible, Visible, Autonomous - that lets agents earn their way into production workflows.",
    date: "March 20, 2026",
    readTime: "5 min read",
    author: { name: "Hetal Mistry", role: "Author" },
    featureImage: trustFeature,
    content: `Most AI deployments start with a lie.

The lie goes like this: "We'll add AI to our delivery workflow, and it'll just work." The coding assistant will know your conventions. The QA agent will understand your failure patterns. The automation will slot into your team's rhythm without friction.

It doesn't happen that way. What actually happens is one of two failure modes - and we've watched both play out across enough engagements to be confident about this.

**Failure mode one: full stealth.** AI tools run behind the scenes, nobody talks about them, nobody governs them. When something breaks - and it always breaks eventually - nobody knows which output was human and which was machine. Accountability disappears. The client loses trust not in the AI, but in the team.

**Failure mode two: full autonomy from day one.** The agents ship code, respond to tickets, push content - all under their own identity, immediately. The team hasn't calibrated. The client hasn't built confidence. One bad output in week two, and the entire AI integration gets pulled. We've seen this happen on a live engagement. The rollback cost more than the original setup.

Both modes share the same root problem: they treat trust as a binary. AI is either hidden or unleashed. There's no middle ground, no progression, no governance.

We needed a middle ground. So we built one.

## Three stages, not a switch

The trust model we operate has three stages: **Invisible, Visible, and Autonomous.** Every AI agent in our delivery system starts at Invisible - no exceptions.

**Invisible** means the agent does real work, but a human operator publishes it. The agent drafts the pull request; the operator reviews, adjusts, and submits it. The agent writes the QA report; the operator signs off. The client sees high-quality output. They don't yet see who - or what - produced the first draft. This isn't deception. It's calibration. The operator is learning the agent's strengths and failure patterns in production conditions, not in a sandbox.

**Visible** means the agent gets a named identity. The client knows "this pull request was drafted by the Code Agent and reviewed by your operator." The agent's work is attributed. Its approval rate is tracked. The client starts seeing the human-machine collaboration directly - and they start trusting it, because the quality has already been proven during the invisible phase.

**Autonomous** means the agent operates with graduated independence. Not unsupervised - graduated. Certain action types that have hit a consistent approval threshold (we use 90%) can proceed without human review. Others still require operator sign-off. Every autonomous action is logged, auditable, and reversible.

The key insight: each stage transition is driven by data, not by schedule. An agent doesn't become Visible because six weeks passed. It becomes Visible because its approval rate crossed the threshold and the operator confirmed readiness. An agent doesn't become Autonomous because someone decided it was time. It becomes Autonomous because the evidence justified it.

## Why this matters beyond methodology

On one engagement - a media technology company with a complex integration stack - we moved from Invisible to Visible in six weeks, on the back of 100% accuracy during the invisible phase. Autonomous came at week eight, with zero regressions. The trust model didn't just govern the AI; it became the mechanism through which the client expanded the engagement. They could see exactly what each agent was doing, how accurate it was, and how much the human operators were adding on top. That transparency drove a 6.4x expansion in scope over 18 months - across HubSpot, middleware, and marketing systems.

The expansion didn't come from a pitch deck. It came from a dashboard.

This is the part most AI-augmented delivery approaches miss entirely. Trust isn't a prerequisite you establish once at kickoff. It's a system you build, measure, and compound over time. When you have real data showing agent approval rates climbing from 60% to 97%, you're not asking the client to believe you. You're showing them.

## What you can take from this

If you're integrating AI agents into delivery - whether that's code generation, QA automation, content ops, or anything else - the question isn't "how capable is the AI?" The question is "how will trust be governed?"

Start invisible. Let the humans calibrate. Make the transition to visibility data-driven, not calendar-driven. And never, ever deploy with full autonomy on day one.

The capability is table stakes now. The governance is the differentiator.`,
    cta: {
      text: "If you're building trust governance into your AI delivery model, we'd be glad to share what we've learned.",
      link: "#",
    },
    lenses: ["engineering", "design"],
    platforms: ["drupal", "acquia"],
    industries: ["higher-ed", "non-profit"],
  },
  {
    slug: "what-we-learned-running-5-agent-pods",
    label: "Delivery",
    title: "What We Learned Running 5 Agent Pods on a 6-Platform Engagement",
    subtitle:
      "An operator dispatch from a multi-platform engagement: how cross-platform intelligence emerges when agents share context - and what breaks when they don't.",
    metaDescription:
      "We ran 5 AI agent pods across 6 platforms on a single engagement. Here's what worked, what broke, and what we'd do differently.",
    excerpt:
      "Running one AI agent on one platform is straightforward. Running five specialist agents across Drupal, Salesforce, HubSpot, AWS, and two more - simultaneously, on a single engagement - is a different problem entirely.",
    date: "March 18, 2026",
    readTime: "6 min read",
    author: { name: "Hetal Mistry", role: "Author" },
    featureImage: agentPodsFeature,
    content: `We didn't plan to run five agent pods at once. The engagement started with two - Code and QA - on the Drupal side of the stack. Within three weeks, the client's platform complexity made it obvious that two pods operating in isolation were creating more coordination overhead than they were saving.

The engagement involved a global certification organization rebuilding their digital platform across six interconnected systems: Drupal for the web experience layer, Salesforce for member management, HubSpot for marketing automation, AWS for infrastructure, plus two additional service integrations. The team had tried running AI coding assistants before. They'd gotten faster individual outputs, but the cross-platform work - where a change in the Salesforce data model affected the Drupal rendering layer which affected the HubSpot personalization logic - was still entirely manual.

That's the gap we were trying to close. Not faster outputs on each platform. Faster *connected* outputs across all of them.

## The five pods

Each agent pod is a specialist. Not a generalist model pointed at a codebase - a purpose-built agent with a constrained scope.

**Code Agent** handled implementation across the Drupal and Salesforce layers. Module scaffolding, API integration patterns, migration scripts. It operated in the codebase daily but never shipped without operator review.

**QA Agent** ran test generation, regression detection, and cross-platform validation. The critical part: it could trace a change in one platform to its expected impact on another and flag when the test coverage didn't match.

**Ops Agent** managed deployment orchestration, environment provisioning, and the operational glue between six platforms that each had their own release cadence. When the Drupal deploy needed to wait for the Salesforce schema migration to propagate, the Ops Agent tracked that dependency graph.

**Comms Agent** handled internal delivery communication - sprint summaries, stakeholder updates, risk flags. Not glamorous, but on a six-platform engagement with multiple workstreams, the coordination overhead was real and growing.

**Context Agent** was the one that changed everything. It ingested from the client's Jira, Slack, GitHub, Zoom, Datadog, and Confluence - building a living knowledge graph that every other agent could query. This is the meta-agent: it doesn't execute delivery tasks, it feeds the intelligence layer that makes every other agent smarter. When the Code Agent was working on a Drupal module, it didn't just have the codebase. It had the Jira ticket history, the Slack thread where the architect explained the constraint, and the Confluence page where the original decision was documented.

## What actually compounded

The compounding effect wasn't theoretical. Here's a concrete example.

The QA Agent flagged a regression in the HubSpot personalization layer. The root cause wasn't in HubSpot - it was a Salesforce field mapping change from two sprints earlier. Without shared context, a human would have spent half a day tracing that dependency. The QA Agent traced it in minutes because the Context Agent had already mapped the relationship between the Salesforce schema change and its downstream consumers.

That pattern repeated dozens of times. A change in one system creating an unexpected ripple in another - caught faster because the agents shared a knowledge graph instead of operating in silos.

The throughput improvement was 3-4x across the engagement, measured against the team's pre-agent velocity on comparable work. Cost per feature dropped 35% - not because the agents were cheap, but because the Context Engine understood how all six platforms interacted, eliminating the cross-platform discovery work that used to eat sprint capacity. But the number that mattered most to the client wasn't throughput - it was the reduction in cross-platform integration defects. Those bugs are expensive. They surface late. They erode trust. The shared context layer caught them early.

## What broke

It wasn't smooth from day one. Three things went wrong that are worth flagging.

First, **context ingestion was noisier than expected.** The client's Slack had years of channels, and the Context Agent initially surfaced outdated architectural decisions as current. We had to build recency weighting and explicit deprecation markers into the knowledge graph. That took a week we hadn't planned for.

Second, **the Ops Agent's dependency tracking assumed cleaner release cadences than reality.** Two of the six platforms had irregular deploy windows controlled by external vendors. The agent modeled dependencies assuming predictable schedules. We had to add manual override checkpoints - the operator could tell the Ops Agent "this deploy is blocked externally, hold the downstream chain."

Third, **operator cognitive load in the first two weeks was high.** Five agents producing output across six platforms means a lot of review surface. We ended up staggering the pod activation - Code and QA first, then Context, then Ops and Comms - rather than lighting everything up at once. That sequencing should have been the plan from the start.

## What you can take from this

If you're running AI agents on a complex, multi-platform engagement, the single highest-leverage investment is shared context. Agents operating in silos will produce faster individual outputs and more integration defects. The context layer is what turns five independent accelerators into a system.

Start with fewer pods than you think you need. Activate them sequentially, not simultaneously. And budget real time for context calibration - the knowledge graph doesn't work on day one. It works on day fifteen, and then it compounds.`,
    cta: {
      text: "Running a multi-platform engagement and thinking about agent infrastructure? We've done it - and we'll tell you what we'd do differently.",
      link: "#",
    },
    lenses: ["engineering", "marketing"],
    platforms: ["aws", "hubspot", "drupal"],
    industries: ["retail", "healthcare"],
  },
  {
    slug: "the-delivery-intelligence-gap",
    label: "Delivery",
    title: "The Delivery Intelligence Gap: Why Faster Tools Aren't Producing Better Outcomes",
    subtitle:
      "The foundational argument for why context - not capability - is the real bottleneck in AI-augmented delivery, and why the industry's current approach is structurally broken.",
    metaDescription:
      "AI tools are faster than ever. Delivery outcomes haven't kept pace. The missing piece isn't capability - it's context. Here's why.",
    excerpt:
      "Every digital team now uses AI coding assistants, testing copilots, and automation scripts. Yet delivery outcomes haven't improved proportionally. The reason is structural: AI operates without context.",
    date: "March 15, 2026",
    readTime: "5 min read",
    author: { name: "Hetal Mistry", role: "Author" },
    featureImage: deliveryGapFeature,
    content: `Here's a pattern we keep seeing. A team adopts Copilot, Cursor, or some other AI coding assistant. Individual developer output goes up. Pull requests arrive faster. Code generation accelerates.

And six months later, delivery timelines haven't meaningfully changed.

The sprint velocity charts look better. The throughput numbers are up. But the actual outcomes - features shipped to production, client satisfaction, defect rates, time-to-value on complex integrations - sit roughly where they were before. Sometimes worse, because the faster individual outputs created more integration work downstream.

This isn't a tooling failure. The tools work. The models are genuinely capable. The problem is structural, and it sits in a layer that most teams haven't built yet.

## The context gap

Every AI tool in a typical delivery workflow operates in isolation. The coding assistant knows the current file and maybe the surrounding codebase. It doesn't know that three months ago, the architect made a deliberate decision to avoid that pattern because of a production incident. It doesn't know that the field it's mapping to in Salesforce was deprecated last sprint. It doesn't know that the client's compliance team requires specific logging on any API that touches PII.

That knowledge lives in Slack threads, Jira ticket histories, Confluence pages, meeting recordings, and - most often - in someone's head. The AI tools can't access it. So they produce outputs that are fast, syntactically correct, and contextually wrong.

The human engineers catch the contextual errors. They review, correct, and adjust. This is the hidden cost of context-free AI: the review burden shifts from "is this code correct?" to "is this code correct *for our specific situation?*" That second question takes longer to answer. It requires more senior attention. And it scales with the number of AI-generated outputs, which means the faster the tools produce, the more review work they create.

We've measured this across engagements. On complex multi-platform work, teams using AI coding assistants without a shared context layer spend 30-40% of senior engineer time on contextual review that wouldn't have been needed if the AI had access to institutional knowledge in the first place.

## Three gaps, not one

The intelligence gap isn't just about context. It's three compounding problems.

**Context gap:** AI doesn't know your institutional history, conventions, constraints, or prior decisions. Every engagement starts from zero. Every agent operates with amnesia.

**Trust gap:** There's no governance model for how AI outputs move from draft to production. Either humans review everything (which negates the speed advantage) or nothing (which creates quality risk). Most teams oscillate between both extremes depending on deadline pressure.

**Transparency gap:** When AI is involved in delivery, nobody can see how. Clients don't know which outputs had AI involvement. Team leads can't tell which agent actions were approved versus autonomous. When something goes wrong, the audit trail doesn't exist.

These three gaps are why faster tools don't produce better outcomes. Speed without context creates more defects. Speed without trust governance creates quality incidents. Speed without transparency erodes the client relationship that the entire engagement depends on.

## The equation that's actually broken

The traditional delivery model calculates output as a function of team size, hours, and skill level. AI tools accelerate the "hours" variable - they make each hour more productive. But the binding constraint on complex delivery was never hours. It was the quality of decisions made during those hours.

A better model: **Output = Operators × Agent Leverage × Context Depth.**

Operators are experienced professionals who know when to trust the agent output and when to override it - AI handles volume, humans handle judgment. Agent leverage is the actual capability of the AI systems. Context depth is the institutional knowledge available to both the human and the AI at the point of decision. Agent leverage scales with the system, not with team size. Context depth compounds over time. The result: the same engagement delivers 3-5x more value as the intelligence matures - because the system gets smarter, not just busier.

Most teams have invested in agent leverage - better tools, more capable models. Almost nobody has invested in context depth. That's the gap.

## What you can take from this

If you're evaluating AI-augmented delivery - whether from a vendor, a partner, or your internal team - ask one question: "Where does the context come from?"

If the answer is "the AI has access to the codebase," that's table stakes. If the answer is "the AI has access to the codebase, the ticket history, the Slack threads, the architectural decisions, the client's compliance constraints, and the production incident log - and all of that compounds over time," that's a different category of delivery entirely.

The tools are fast enough. The models are capable enough. The gap is context. Close that, and the rest follows.`,
    cta: {
      text: "If you're seeing fast tools but flat outcomes, the bottleneck is probably where you think it is. We've built the context layer.",
      link: "#",
    },
    lenses: ["engineering", "marketing", "design"],
    platforms: ["acquia", "aws"],
    industries: ["higher-ed", "healthcare", "retail"],
  },
];

export function getBlogBySlug(slug: string): BlogPost | undefined {
  return blogPosts.find((p) => p.slug === slug);
}
